import React, { useState, useContext } from "react"

import { GetGroups, GetGroup, GroupI } from "../../services/groups";
import { Context } from "../../models/context";
import { Project } from "../../models/project";
import Modal from "../modal/Modal";

import "./project-settings-modal.css";
import { useAlerts } from "../../contexts/AlertContext";

const NUM_SUGGESTIONS = 3;

interface ProjectSettingsModalProps {
    isOpen: boolean;
    project: Project;
    creating: boolean;
    onClose: () => void;
    updateProject: (project: Project, newProject: Project) => void;
}

export default function ProjectSettingsModal({ project, isOpen, creating, onClose, updateProject }: ProjectSettingsModalProps) {
    const [changesMade, setChangesMade] = useState(false);
    const [newProject, setNewProject] = useState(Project.ensureInstance(project).copy());
    const [groupInputSelected, setGroupInputSelected] = useState(false);
    const [showGroupSuggestions, setShowGroupSuggestions] = useState(false);
    const [groupSuggestions, setGroupSuggestions] = useState<GroupI[]>([]);

    const [groupIdToName,setGroupIdToName] = useState<Record<string,string>>({});

    const context = useContext(Context);
    const {addError} = useAlerts();

    const deleteProject = () => { 
        if (window.confirm("Are you sure you want to delete this project? This action cannot be undone")) {
            {/* TODO: Implementate delete a project */}
        }
    }

    const submitChanges = () => {
        updateProject(project, newProject);
        setChangesMade(false);
        onClose();
    }

    const cancel = () => {
        if (changesMade) {
            if (!window.confirm("Are you sure you want to discard your changes?")) {
                return;
            } else {
                setNewProject(project.copy());
                setChangesMade(false);
            }
        }
        onClose();
    }

    const addGroup = (group: string) => {
        setNewProject(p => {
            const pCopy = p.copy();
            pCopy.groups.add(group);
            return pCopy;
        });
        setGroupSuggestions([]);
        setChangesMade(true);
        addNameForGroup(group);
    }

    const removeGroup = (group: string) => {
        setNewProject(p => {
            const pCopy = p.copy();
            pCopy.groups.delete(group);
            return pCopy;
        });
        setChangesMade(true);
    }

    const completeProject = () => {
        if (window.confirm("Are you sure you want to mark this project as completed? This action cannot be undone.")) {
            const completedProject = project.copy();
            completedProject.completed = true;
            setNewProject(completedProject);
            updateProject(project, completedProject);
            setChangesMade(false);
            onClose();
        }
    }

    const handleGroupInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.value) {
            setGroupSuggestions([]);
            return;
        }

        const service = new GetGroups({ count: NUM_SUGGESTIONS, name: e.target.value });
        service.request(context.user!).then((groups: GroupI[]) => {
            const filtered_groups=groups.filter(g => !newProject.groups.has(g.groupId));
            const groupIds = filtered_groups.map(g => g.groupId);
            console.log("Got group suggestions: ", groupIds);
            setGroupSuggestions(filtered_groups);
            // update user with group permissions
            const modifiedUser = context.user;
            if (modifiedUser) {
                groups.forEach(g => {
                    if (!modifiedUser.groupPermissions) modifiedUser.groupPermissions = {};
                    modifiedUser.groupPermissions[g.groupId] = new Set(g.userStatus);
                });
                context.setUser(modifiedUser);
            }
        }).catch(error => {
            console.error("Error fetching group suggestions: ", error);
            addError(error as Error, "Error fetching group suggestions")
            setGroupSuggestions([]);
        });
    }
    const addNameForGroup = (id: string)=>{
        new GetGroup(id).request(context.user!).then((group)=>{
                setGroupIdToName ({
                    ...groupIdToName,
                    id: group.groupName,
                });
            }).catch(e=>addError(e as Error,"Error getting group"))
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => cancel()}
            title={`${creating ? "Create" : "Edit"} ${project.name}`}
            closeOnOverlayClick={false}
        >
            <div className="project-settings-modal-content">
                <div id="name-setting" className="project-setting">
                    <label htmlFor={`${project.name}-name-input`}>Project Name: </label>
                    <input
                        id={`${project.name}-name-input`}
                        type="text"
                        className="project-settings-input"
                        value={newProject.name}
                        onChange={(e) => {
                            setNewProject(p => {
                                const pCopy = p.copy();
                                pCopy.name = e.target.value;
                                return pCopy;
                            });
                            setChangesMade(true);
                        }}
                    />
                </div>
                <div id="description-setting" className="project-setting">
                    <label htmlFor={`${project.name}-description-input`}>Description: </label>
                    <textarea
                        id={`${project.name}-description-input`}
                        className="project-settings-input"
                        value={newProject.description}
                        onChange={(e) => {
                            setNewProject(p => {
                                const pCopy = p.copy();
                                pCopy.description = e.target.value;
                                return pCopy;
                            });
                            setChangesMade(true);
                        }}
                    />
                </div>
                <div id="threshold-setting" className="project-setting">
                    <label
                        className="project-settings-label"
                        htmlFor={`${project.name}-threshold-input`}
                    >
                        Threshold:
                    </label>
                    <input
                        id={`${project.name}-threshold-input`}
                        type="number"
                        className="project-settings-input"
                        defaultValue={project.Threshold}
                        onChange={(e) => {
                            setNewProject(p => {
                                const pCopy = p.copy();
                                pCopy.Threshold = e.target.value ? parseInt(e.target.value) : undefined;
                                return pCopy;
                            })
                            setChangesMade(true)
                        }}
                    />
                </div>
                <div id="groups-setting" className="project-setting">
                    <label className="project-settings-label">Groups: </label>
                    {newProject.groups.size > 0 && (<>
                        <div className="groups-list">
                            {Array.from(newProject.groups).map((group) => (
                                <div key={`group-${group}`} className="group-item">
                                    <span className="group-item-name">{groupIdToName[group]}</span>
                                    <button
                                        type="button"
                                        className="group-item-remove"
                                        aria-label={`Remove ${groupIdToName[group]}`}
                                        onClick={() => removeGroup(group)}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div></div>
                    </>)}
                    <div className="group-input-wrapper">
                        <input
                            type="text"
                            className="project-settings-input"
                            id={`group-settings-input`}
                            placeholder="Add group"
                            onFocus={() => setGroupInputSelected(true)}
                            onBlur={() => setGroupInputSelected(false)}
                            onChange={handleGroupInput}
                        />
                        {groupSuggestions.length > 0 && (showGroupSuggestions || groupInputSelected) && (
                            <div id="group-suggestions"
                                onMouseEnter={() => setShowGroupSuggestions(true)}
                                onMouseLeave={() => setShowGroupSuggestions(false)}
                            >
                                {groupSuggestions.map((suggestion, index) => (
                                    <div
                                        key={`suggestion-${index}`}
                                        className="group-suggestion"
                                        onClick={() => addGroup(suggestion.groupId)}
                                    >
                                        {suggestion.groupName}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {newProject.groups.size > 0 && (<>
                        <div className="groups-list">
                            {Array.from(newProject.groups).map((group, index) => (
                                <div key={`group-${index}`} className="group-item">
                                    <span>{group}</span>
                                </div>
                            ))}
                        </div>
                    </>)}
                </div>

                <div id="regen-setting" className="project-setting" >
                    <div id="regen-secondary">
                        <label>
                            Auto-Regeneration:
                            <span className="switch">
                                <input
                                    type="checkbox"

                                    className={`project-settings-input ${newProject.regeneration ? "regen-on" : "regen-off"}`}
                                    id="regen-setting-input"
                                    checked={newProject.regeneration}
                                    onChange={() => {
                                        setNewProject(p => {
                                            const pCopy = Project.ensureInstance(p).copy();
                                            pCopy.toggleRegen();
                                            return pCopy;
                                        });
                                        setChangesMade(true);
                                    }}
                                />
                                <span className="slider" />
                            </span>
                        </label>
                    </div>
                </div>
                
                <div id="public-setting" className="project-setting">
                    <div id="public-secondary">
                        <label>
                            Public:
                            <span className="switch">
                                <input
                                    type="checkbox"
                                    className={`project-settings-input ${newProject.isPublic ? "regen-on" : "regen-off"}`}
                                    id="public-setting-input"
                                    checked={newProject.isPublic}
                                    onChange={() => {
                                        setNewProject(p => {
                                            const pCopy = Project.ensureInstance(p).copy();
                                            pCopy.isPublic = !pCopy.isPublic;
                                            return pCopy;
                                        });
                                        setChangesMade(true);
                                    }}
                                />
                                <span className="slider" />
                            </span>
                        </label>
                    </div>
                </div>
                
                <input className="submit-button" id={`${project.name}-submit`} type="button" value="Save" onClick={submitChanges} disabled={!changesMade} />
                <div id="changing-buttons">
                    {!creating && <input id="delete-btn" type="button" value="Delete" onClick={() => deleteProject()} />} 
                    {!creating && <input className="complete-button" id={`${project.name}-complete`} type="button" value="Complete Project" onClick={completeProject} />}
                </div>




            </div>
        </Modal>
    )
}
